import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, StatusBadge, type DataColumn, type Status } from '../../components/ui';
import type { AutomationAnalyticsSummary } from '../../lib/api';
import { formatNumber, formatPercent } from '../../lib/format';

/** The Statistiques figures and the per-automation table (R29, `R29-Analytics.dc.html`). */

// --- totals ---------------------------------------------------------------------------------

/** The KPI row (DS `.grid-kpi`): as many cards per line as fit at 140px, so two on any phone from 360px wide (a Samsung S2x included). */
export const KPI_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(min(140px,100%),1fr))] gap-3';

export interface Totals {
  executions: number;
  completed: number;
  failed: number;
  messages: number;
  reached: number;
}

export function totalsOf(rows: AutomationAnalyticsSummary[] | undefined): Totals | undefined {
  return rows?.reduce<Totals>(
    (t, r) => ({
      executions: t.executions + r.executionsTotal,
      completed: t.completed + r.executionsCompleted,
      failed: t.failed + r.executionsFailed,
      messages: t.messages + r.messagesSent,
      reached: t.reached + r.contactsReached,
    }),
    { executions: 0, completed: 0, failed: 0, messages: 0, reached: 0 },
  );
}

/** Completed out of finished runs: waiting and running ones have no outcome yet. */
export function successRate(completed: number, failed: number): number | null {
  const finished = completed + failed;
  return finished === 0 ? null : completed / finished;
}

/** Below this many finished runs a percentage overstates what is known: "2/3" instead of "66,7 %" (GLOSSARY-FR §3). */
export const SMALL_SAMPLE = 5;

/** The success figure as shown: "2/3" for a small sample, "90 %" from five finished runs, null with none. */
export function successFigure(completed: number, failed: number): string | null {
  const finished = completed + failed;
  if (finished === 0) return null;
  return finished < SMALL_SAMPLE ? `${formatNumber(completed)}/${formatNumber(finished)}` : formatPercent(completed / finished);
}

// --- per automation ------------------------------------------------------------------------

/** The glossary's badge (GLOSSARY-FR §2); "Archived" is not a glossary status and keeps a neutral pill. */
const GLOSSARY_STATUSES: Record<string, Status> = { active: 'active', paused: 'paused', draft: 'draft' };

export function AutomationStatus({ status }: { status: string }) {
  const { t } = useTranslation();
  const known = GLOSSARY_STATUSES[status];
  if (known) return <StatusBadge status={known} />;
  return <Badge>{status === 'archived' ? t('analytics.archived') : status}</Badge>;
}

/** Busiest first. */
export const byExecutions = (a: AutomationAnalyticsSummary, b: AutomationAnalyticsSummary) => b.executionsTotal - a.executionsTotal;

export const detailPath = (row: AutomationAnalyticsSummary) => `/analytics/automations/${row.automationId}`;

/** "—" for an automation that was never active, "0" for a measured nothing; both muted (the table footer says so). */
const isDraft = (row: AutomationAnalyticsSummary) => row.status === 'draft';
const MUTED = 'text-ink-3';

function Count({ row, value }: { row: AutomationAnalyticsSummary; value: number }) {
  if (isDraft(row)) return <span className={MUTED}>—</span>;
  return <span className={value === 0 ? MUTED : 'text-ink'}>{formatNumber(value)}</span>;
}

/** "2/3" in red with "1 échec" beside it, "3/3" in green; a ratio under five runs (R29.5). */
function Success({ row }: { row: AutomationAnalyticsSummary }) {
  const { t } = useTranslation();
  const figure = isDraft(row) ? null : successFigure(row.executionsCompleted, row.executionsFailed);
  if (figure === null) return <span className={MUTED}>—</span>;
  const failed = row.executionsFailed > 0;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className={`tabular font-semibold ${failed ? 'text-danger-700' : 'text-success-700'}`}>{figure}</span>
      {failed ? <Badge tone="err">{t('analytics.failures', { count: row.executionsFailed })}</Badge> : null}
    </span>
  );
}

/** One column instead of two empty ones (R29.4, A12-01): the count and rate with a goal, a link to set one without. */
function Conversions({ row }: { row: AutomationAnalyticsSummary }) {
  const { t } = useTranslation();
  if (isDraft(row)) return <span className={MUTED}>—</span>;
  if (row.conversions === null) {
    return (
      <Link to={`${detailPath(row)}#objectif`} className="text-ds-sm font-semibold text-primary-700 hover:underline">
        {t('analytics.table.setGoal')}
      </Link>
    );
  }
  return (
    <span className="tabular text-ink">
      {formatNumber(row.conversions)}
      {row.conversionRate !== null ? <span className="text-ink-3"> · {formatPercent(row.conversionRate)}</span> : null}
    </span>
  );
}

export function useAnalyticsColumns(): DataColumn<AutomationAnalyticsSummary>[] {
  const { t } = useTranslation();
  return [
    {
      key: 'automation',
      header: t('analytics.table.automation'),
      cell: (row) => (
        <Link to={detailPath(row)} className="group inline-flex max-w-full items-center gap-2 text-ink">
          <span className="truncate font-semibold group-hover:underline">{row.name}</span>
          <AutomationStatus status={row.status} />
        </Link>
      ),
    },
    {
      key: 'fired',
      header: t('analytics.table.fired'),
      align: 'end',
      width: 'w-[136px]',
      cell: (row) => <Count row={row} value={row.executionsTotal} />,
      narrow: false,
    },
    {
      key: 'success',
      header: t('analytics.table.success'),
      width: 'w-[150px]',
      cell: (row) => <Success row={row} />,
      narrowCell: (row) =>
        isDraft(row) ? (
          <span className={MUTED}>—</span>
        ) : (
          <>
            <span className="text-ink-2">{t('dashboard.health.fired', { count: row.executionsTotal })}</span>
            {row.executionsTotal > 0 ? <span aria-hidden className={MUTED}>·</span> : null}
            {row.executionsTotal > 0 ? <Success row={row} /> : null}
          </>
        ),
    },
    {
      key: 'messages',
      header: t('analytics.table.messages'),
      align: 'end',
      width: 'w-[100px]',
      cell: (row) => <Count row={row} value={row.messagesSent} />,
      narrow: false,
    },
    {
      key: 'reached',
      header: t('analytics.table.reached'),
      align: 'end',
      width: 'w-[148px]',
      cell: (row) => <Count row={row} value={row.contactsReached} />,
      narrow: false,
    },
    {
      key: 'conversions',
      header: t('analytics.table.conversions'),
      width: 'w-[168px]',
      cell: (row) => <Conversions row={row} />,
      narrowCell: (row) =>
        isDraft(row) ? null : row.conversionRate !== null ? (
          <span className="text-ink-2">{t('analytics.table.converted', { rate: formatPercent(row.conversionRate) })}</span>
        ) : (
          <Conversions row={row} />
        ),
    },
  ];
}
